import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { Navigate } from 'react-router-dom'

import { Spinner } from '~/shadcn/ui/spinner'
import { getApiErrorMessage } from '~/@lib/utils/error.utils'
import Analytics from '~/@lib_integrations/analytics/analytics'
import { useLocalNotifications } from '~/@mid_ui/local-notifications/useLocalNotifications'
import { useLogin } from '~/@pages_auth/login/login-form/useLogin'
import { useUser } from '~/@biz_account/useUser/useUser'
import { getInvestorAnalysisVersion } from '../analysis-versions/investorAnalysisVersions'
import { LowTestScore } from '../stage-analysis/LowTestScore'
import { useInvestorAnalysisStore } from '../stage-analysis/useInvestorAnalysisStore'
import { UnfinishedAnalysisDialog } from '../stage-analysis/UnfinishedAnalysisDialog'
import { useInvestorAnalysis } from '../useInvestorAnalysis'

type Props = {
  onAnalysisCompleted: () => Promise<void>
}

function InvestorAnalysis(props: Props) {
  const {
    InitialScreen,
    AssessExperienceStage,
    SophisticatedTest,
    UnsophisticatedTest,
    steps,
    useSophisticatedTestStore,
    useUnsophisticatedTestStore,
    QuestionsProvider,
    buildAnswers,
  } = getInvestorAnalysisVersion()
  const { evaluateInvestorAnalysis, finishInvestorAnalysis } =
    useInvestorAnalysis()
  const { setNotification } = useLocalNotifications()
  const { logoutUser } = useLogin()
  const { user } = useUser()
  const analysisStore = useInvestorAnalysisStore(user?._id ?? null)
  const unsophisticatedTestStore = useUnsophisticatedTestStore(
    user?._id ?? null
  )
  const sophisticatedTestStore = useSophisticatedTestStore(user?._id ?? null)

  const evaluateInvestorAnalysisMutation = useMutation({
    mutationFn: evaluateInvestorAnalysis,
  })
  const finishInvestorAnalysisMutation = useMutation({
    mutationFn: finishInvestorAnalysis,
  })
  const [unfinishedModalOpen, setUnfinishedModalOpen] = useState(false)

  function closeUnfinishedModal() {
    setUnfinishedModalOpen(false)
  }
  function openUnfinishedModal() {
    setUnfinishedModalOpen(true)
  }

  function resetInvestorAnalysisFormSteps() {
    unsophisticatedTestStore.reset()
    sophisticatedTestStore.reset()
  }

  function resetInvestorAnalysis() {
    analysisStore.reset()
    resetInvestorAnalysisFormSteps()
  }

  type AnswersParams = Parameters<typeof buildAnswers>[0]
  type Answers = ReturnType<typeof buildAnswers>
  type Evaluation = Awaited<ReturnType<typeof evaluateInvestorAnalysis>>

  // `testData` z posledného kroku ešte nie je v renderi zachytenom store, preto ho
  // `onCompleted` posiela priamo.
  function buildCurrentAnswers(
    testData: Partial<
      Pick<AnswersParams, 'sophisticatedTestData' | 'unsophisticatedTestData'>
    > = {}
  ) {
    return buildAnswers({
      isSophisticatedInvestor: analysisStore.isSophisticatedInvestor,
      sophisticatedTestData:
        testData.sophisticatedTestData ?? sophisticatedTestStore.testData,
      unsophisticatedTestData:
        testData.unsophisticatedTestData ?? unsophisticatedTestStore.testData,
    })
  }

  // typ investora a skóre vyhodnocuje API, FE podľa výsledku len vetví dotazník
  function evaluateAnswers(
    answers: Answers,
    onEvaluated: (evaluation: Evaluation) => void
  ) {
    evaluateInvestorAnalysisMutation.mutate(
      { answers },
      {
        onSuccess: onEvaluated,
        onError: (error) => {
          setNotification(
            'error',
            getApiErrorMessage(
              error,
              'Investičný dotazník sa nepodarilo vyhodnotiť.'
            )
          )
        },
      }
    )
  }

  // `riskWarningConfirmed` = user pri skóre F potvrdil obrazovku „Vysoké investičné riziko“
  function handleInvestorAnalysisCompleted(
    answers: Answers,
    riskWarningConfirmed = false
  ) {
    finishInvestorAnalysisMutation.mutate(
      { answers, riskWarningConfirmed },
      {
        onSuccess: () => {
          Analytics.track('user-completed-investor-analysis')
          props.onAnalysisCompleted()
        },
        onError: (error) => {
          setNotification(
            'error',
            getApiErrorMessage(
              error,
              'Investičný dotazník sa nepodarilo dokončiť.'
            )
          )
        },
      }
    )
  }

  function handleUnsophisticatedTestCompleted(
    testData: AnswersParams['unsophisticatedTestData']
  ) {
    const answers = buildCurrentAnswers({ unsophisticatedTestData: testData })
    evaluateAnswers(answers, ({ score }) => {
      if (score === 'F') {
        analysisStore.setStage('lowTestScore')
        return
      }

      handleInvestorAnalysisCompleted(answers)
    })
  }

  const renderedContent = (() => {
    if (
      evaluateInvestorAnalysisMutation.isPending ||
      finishInvestorAnalysisMutation.isPending
    ) {
      return (
        <div className="flex justify-center items-center h-full">
          <Spinner className="size-10" />
        </div>
      )
    }

    const { finishedScreen, stage } = analysisStore

    if (finishedScreen) {
      return <Navigate to="/investor-signature" />
    }

    if (stage === 'initial') {
      return (
        <InitialScreen
          onLeave={openUnfinishedModal}
          onTakeTest={(isSophisticatedInvestor) => {
            window.scrollTo(0, 0)
            resetInvestorAnalysisFormSteps()

            analysisStore.setIsSophisticatedInvestor(isSophisticatedInvestor)
            analysisStore.setStage(
              isSophisticatedInvestor ? 'sophisticated' : 'assessExperience'
            )
          }}
        />
      )
    }

    if (stage === 'sophisticated') {
      return (
        <SophisticatedTest
          onLeave={() => analysisStore.setStage('initial')}
          onCompleted={(testData) => {
            const answers = buildCurrentAnswers({
              sophisticatedTestData: testData,
            })
            evaluateAnswers(answers, ({ isSophisticated }) => {
              if (isSophisticated) {
                handleInvestorAnalysisCompleted(answers)
                return
              }

              analysisStore.setStage('assessExperience')
            })
          }}
        />
      )
    }

    if (stage === 'assessExperience') {
      const wentThroughSophisticatedTest =
        !!sophisticatedTestStore.currentStepIndex
      return (
        <AssessExperienceStage
          showNeedMoreInfo={wentThroughSophisticatedTest}
          onGoBack={() =>
            analysisStore.setStage(
              wentThroughSophisticatedTest ? 'sophisticated' : 'initial'
            )
          }
        />
      )
    }

    if (stage === 'experienced') {
      return (
        <UnsophisticatedTest
          steps={steps.experienced}
          onLeave={() => analysisStore.setStage('assessExperience')}
          onCompleted={handleUnsophisticatedTestCompleted}
        />
      )
    }

    if (stage === 'inexperienced') {
      return (
        <UnsophisticatedTest
          steps={steps.inexperienced}
          onLeave={() => analysisStore.setStage('assessExperience')}
          onCompleted={handleUnsophisticatedTestCompleted}
        />
      )
    }

    if (stage === 'lowTestScore') {
      return (
        <LowTestScore
          onLeave={logoutUser}
          onRetakeTest={resetInvestorAnalysis}
          onContinue={() => {
            handleInvestorAnalysisCompleted(buildCurrentAnswers(), true)
          }}
        />
      )
    }

    return null
  })()

  return (
    <QuestionsProvider>
      {renderedContent}{' '}
      {unfinishedModalOpen && (
        <UnfinishedAnalysisDialog onClose={closeUnfinishedModal} />
      )}
    </QuestionsProvider>
  )
}

export { InvestorAnalysis }
